<template>
  <div
    id="chemical-containers"
    class="chemtracker-module"
    data-test="outside-vif"
  >
    <vue-progress-bar />
    <RAFTFullScreenContent
      @openClose="updateOpenClosedFullscreen"
      header-text="Chemical Containers"
    >
      <template #pager>
        <div class="view-pager-total" role="status" aria-live="polite">
          {{ displayAtoB }}
          <strong>{{ totalContainers }}</strong> results
        </div>
      </template>
      <template #topleft>
        <VActionButton
          :show-badge="false"
          :show-icon="false"
          label="Download"
          @click="postToCsvDownload"
        >
          <img
            id="csv-export-help"
            :src="helpSrc"
            alt="help"
            v-if="!dromoEnabled"
          />
        </VActionButton>
        <b-popover target="csv-export-help" triggers="hover focus">
          <template slot="title"> Data Export</template>
          This export is only for use of data outside of the system. Any changes
          to chemical inventory should be made directly here.
        </b-popover>
        <DromoImportButton v-if="showDromoButton" />
      </template>
      <template #topright>
        <VActionButton
          :show-badge="true"
          :show-icon="false"
          label="Filters"
          :is-pressed="showFilters"
          :count="adjustedFilterCount"
          @click="toggleShowFilters"
        />
        <VActionButton
          :show-badge="false"
          :show-icon="false"
          label="Settings"
          :is-pressed="showSettings"
          @click="toggleShowSettings"
        />
      </template>
      <div
        style="
          font-size: 1rem;
          color: var(--blue-900, #2964d4);
          font-weight: 500;
          padding: 0.75rem;
          margin: 0.5rem 0;
          background: #e3f1fe;
          border-radius: 2px;
          display: flex;
          align-items: center;
          gap: 0.25rem;
        "
        v-if="showSettings"
      >
        <span
          ><img :src="infoSrc" alt="info" /> You can uncheck columns to hide
          them, and drag to rearrange.
          <template v-if="changesMade">
            <!-- Click 'Settings' to apply your changes. -->
          </template></span
        >
        <a href="#" @click="resetDefaults" data-test="reset-defaults"
          >Reset Defaults</a
        >
      </div>
      <VFilterGroup
        v-if="showFilters"
        :show-submit="false"
        @clear-filters="clearFilters"
      >
        <template #filters>
          <short-text-filter
            id="chemical-name"
            data-test="chemical-name"
            label="Chemical Name"
            class="chemtracker-container-text-filter"
            store-watch="getChemicalNameFilter"
            path="field_chemical_name_preferred"
            filter-key="chemicalNameFilter"
            filter-action="updateFilter"
            @update-filter="($e) => debouncedDispatchFilter($e)"
          />
          <short-text-filter
            id="cas-num"
            data-test="cas-num"
            class="chemtracker-container-text-filter"
            store-watch="getCasNumberFilter"
            path="field_chemical_type.field_chemdb_cas_number"
            label="CAS Number"
            filter-key="casNumberFilter"
            filter-action="updateFilter"
            @update-filter="($e) => debouncedDispatchFilter($e)"
          />
          <short-text-filter
            id="chem-syn-fil"
            data-test="chem-syn-fil"
            label="Chemical Synonym"
            class="chemtracker-container-text-filter"
            store-watch="getChemicalSynonymFilter"
            path="field_chemical_type.field_chemdb_synonyms"
            store-commit="setChemicalSynonymFilter"
            filter-key="chemicalSynonymFilter"
            filter-action="updateChemicalSynonymFilter"
            @update-filter="($e) => debouncedDispatchFilter($e)"
          />
          <multi-select-filter
            v-if="getLabCheck"
            :get-value="allLabs"
            id="lab-fil"
            data-test="lab-fil"
            label="Group Name"
            display-value="title"
            active-status="operative_status"
            track-by="nid"
            store-watch="getSelectedLabsFilter"
            path="og_audience.drupal_internal__nid"
            operator="IN"
            filter-key="selectedLabs"
            filter-action="updateMultivalueFilter"
            @update-filter="($e) => debouncedDispatchFilter($e)"
          />
          <multi-select-filter
            v-if="getBuildingCheck"
            :get-value="allBuildings"
            id="building-fil"
            data-test="building-fil"
            label="Building"
            store-watch="getSelectedBuildingsFilter"
            display-value="title"
            track-by="nid"
            path="field_chemical_space.field_parent_location.drupal_internal__nid"
            filter-key="selectedBuildings"
            filter-action="updateSelectedBuildings"
            @update-filter="($e) => debouncedDispatchFilter($e)"
          />
          <multi-select-filter
            v-if="getSpaceCheck"
            :get-value="allSpaces"
            id="space-fil"
            data-test="space-fil"
            label="Spaces"
            display-value="title"
            store-watch="getSelectedSpacesFilter"
            path="field_chemical_space.drupal_internal__nid"
            track-by="drupal_internal__nid"
            :disable="getSpaceFilterDisabled"
            filter-key="selectedSpaces"
            filter-action="updateMultivalueFilter"
            @update-filter="($e) => debouncedDispatchFilter($e)"
          />
        </template>
        <template #advanced-filters>
          <multi-select-filter
            :get-value="getChemOwningUserOptions"
            id="chem-owner-fil"
            :hovertip="chemicalOwnerHovertip"
            data-test="chem-owner-fil"
            label="Chemical Owner"
            display-value="field_prof_display_name_value"
            store-watch="getSelectedChemOwningUsers"
            path="field_chemical_container_owner.meta.drupal_internal__target_id"
            operator="IN"
            track-by="uid"
            filter-key="selectedChemicalOwningUsers"
            filter-action="updateMultivalueFilter"
            :options-limit="chemicalOwnerOptionsLimit"
            @update-filter="($e) => debouncedDispatchFilter($e)"
          />
          <multi-select-filter
            :get-value="physicalStates"
            id="state-fil"
            data-test="state-fil"
            label="Physical State"
            display-value="label"
            store-watch="getSelectedPhysicalStatesFilter"
            path="field_chemical_type.field_chemdb_physical_state"
            operator="IN"
            track-by="key"
            filter-key="selectedPhysicalStates"
            filter-action="updateMultivalueFilter"
            @update-filter="($e) => debouncedDispatchFilter($e)"
          />
          <multi-select-filter
            v-if="getBuildingCheck"
            :get-value="allDepartments"
            id="dept-fil"
            data-test="dept-fil"
            label="Department/Unit"
            display-value="title"
            track-by="nid"
            store-watch="getSelectedDepartmentsFilter"
            path="og_audience.og_audience.drupal_internal__nid"
            operator="IN"
            filter-key="selectedDepartments"
            filter-action="updateMultivalueFilter"
            @update-filter="($e) => debouncedDispatchFilter($e)"
          />
          <multi-select-filter
            v-if="getBuildingCheck"
            :get-value="allCampuses"
            :hide-empty="true"
            id="campus-fil"
            data-test="campus-fil"
            label="Site/Campus"
            display-value="title"
            track-by="nid"
            store-watch="getSelectedCampusesFilter"
            path="field_chemical_space.field_parent_location.field_parent_location.drupal_internal__nid"
            filter-key="selectedCampuses"
            filter-action="updateMultivalueFilter"
            @update-filter="($e) => debouncedDispatchFilter($e)"
          />
          <date-filter
            wrapper-class="update-after-filter"
            :get-value="getUpdatedAfterDateFilter"
            id="last-update-after-fil"
            data-test="last-update-after-fil"
            placeholder=""
            label="Last Updated After"
            :hovertip="lastUpdateAfterHovertip"
            input-class="chemtracker-container-date-filter"
            filter-key="updateAfterDateFilter"
            filter-action="updateUpdatedAfterDateFilter"
            @update-filter="($e) => debouncedDispatchFilter($e)"
          />
          <date-filter
            :get-value="getUpdatedBeforeDateFilter"
            :update-method="updateUpdatedBeforeDateFilter"
            id="last-update-before-fil"
            data-test="last-update-before-fil"
            placeholder=""
            name="Last Updated Before"
            label="Last Updated Before"
            input-class="chemtracker-container-date-filter"
            filter-key="updateBeforeDateFilter"
            filter-action="updateUpdatedBeforeDateFilter"
            @update-filter="($e) => debouncedDispatchFilter($e)"
          />
          <short-text-filter
            id="product-filter"
            data-test="product-filter"
            name="product-filter"
            label="Product Name"
            class="chemtracker-container-text-filter"
            store-watch="getChemicalProductFilter"
            path="field_chemical_product_name"
            filter-key="chemicalProductFilter"
            filter-action="updateFilter"
            @update-filter="($e) => debouncedDispatchFilter($e)"
          />
          <date-filter
            :get-value="getContainerExpiresAfterFilter"
            id="container-expires-after-fil"
            data-test="container-expires-after-fil"
            name="container-expires-after-fil"
            placeholder=""
            label="Container Expires After"
            input-class="chemtracker-container-date-filter"
            filter-key="containerExpiresAfterDateFilter"
            filter-action="updateContainerExpiresAfterFilter"
            @update-filter="($e) => debouncedDispatchFilter($e)"
          />
          <date-filter
            :get-value="getContainerExpiresBeforeFilter"
            id="container-expires-before-fil"
            data-test="container-expires-before-fil"
            name="container-expires-before-fil"
            placeholder=""
            label="Container Expires Before"
            input-class="chemtracker-container-date-filter"
            filter-key="containerExpiresBeforeDateFilter"
            filter-action="updateContainerExpiresBeforeFilter"
            @update-filter="($e) => debouncedDispatchFilter($e)"
          />
          <multi-select-filter
            :get-value="allChemicalHazards"
            id="hazard-code-fil"
            data-test="hazard-code-fil"
            label="Chemical Hazards"
            display-value="label"
            track-by="key"
            store-watch="getSelectedHazardCodesFilter"
            store-commit="setSelectedHazardCodesFilter"
            path="field_chemical_type.field_chemdb_hazard_code"
            operator="IN"
            filter-key="selectedHazardCodes"
            filter-action="updateMultivalueFilter"
            @update-filter="($e) => debouncedDispatchFilter($e)"
          />
          <multi-select-filter
            :get-value="dataBaseLinkageStatuses"
            id="database-link-fil"
            data-test="database-link-fil"
            label="Database Linkage Status"
            display-value="label"
            track-by="key"
            :multiple="false"
            store-watch="getSelectedDatabaseLinkageFilter"
            :hovertip="databaseLinkageStatusHovertip"
            wrapper-class="chemical-database-linkage-filter"
            filter-key="selectedDatabaseLinkage"
            filter-action="updateSelectedDatabaseLinkageFilter"
            @update-filter="($e) => debouncedDispatchFilter($e)"
          />
          <multi-select-filter
            :get-value="storageGroups"
            id="storage-group-fil"
            data-test="storage-group-fil"
            label="Storage Group"
            display-value="label"
            track-by="key"
            :multiple="true"
            store-watch="getSelectedStorageGroupFilter"
            path="field_chemical_type.field_chemdb_storage_group"
            filter-key="selectedStorageGroup"
            filter-action="updateMultivalueFilter"
            @update-filter="($e) => debouncedDispatchFilter($e)"
          />
          <short-text-filter
            id="bench-fil"
            data-test="bench-fil"
            label="Bench"
            class="chemtracker-container-text-filter"
            store-watch="getBenchFilter"
            path="field_chemical_bench_location"
            filter-key="benchFilter"
            filter-action="updateFilter"
            @update-filter="($e) => debouncedDispatchFilter($e)"
          />
          <short-text-filter
            id="shelf-fil"
            data-test="shelf-fil"
            label="Shelf"
            class="chemtracker-container-text-filter"
            store-watch="getShelfFilter"
            path="field_chemical_shelf_location"
            filter-key="shelfFilter"
            filter-action="updateFilter"
            @update-filter="($e) => debouncedDispatchFilter($e)"
          />
          <short-text-filter
            id="sln-fil"
            data-test="sln-fil"
            label="Specific Location Note"
            class="chemtracker-container-text-filter"
            store-watch="getSpecificLocationNoteFilter"
            path="field_chemical_room_location"
            filter-key="specificLocationNoteFilter"
            filter-action="updateFilter"
            @update-filter="($e) => debouncedDispatchFilter($e)"
          />
          <template v-if="sds_enabled">
            <multi-select-filter
              :get-value="sdsOriginStatuses"
              id="sds-origin-fil"
              data-test="sds-origin-fil"
              label="SDS Origin"
              display-value="label"
              track-by="key"
              :multiple="false"
              store-watch="getSdsOriginFilter"
              wrapper-class="chemical-sds-origin-filter"
              filter-key="sdsOriginFilter"
              filter-action="updateSdsOriginFilter"
              @update-filter="($e) => debouncedDispatchFilter($e)"
            />
            <date-filter
              :get-value="getSdsDocumentDateAfterFilter"
              id="sds-document-after-fil"
              data-test="sds-document-after-fil"
              name="sds-document-after-fil"
              placeholder=""
              label="SDS Document Date After"
              input-class="chemtracker-container-date-filter"
              filter-key="sdsDocumentDateAfterFilter"
              filter-action="updateSdsDocumentDateAfterFilter"
              @update-filter="($e) => debouncedDispatchFilter($e)"
            />
            <date-filter
              :get-value="getSdsDocumentDateBeforeFilter"
              id="sds-document-before-fil"
              data-test="sds-document-before-fil"
              name="sds-document-before-fil"
              placeholder=""
              label="SDS Document Date Before"
              input-class="chemtracker-container-date-filter"
              filter-key="sdsDocumentDateBeforeFilter"
              filter-action="updateSdsDocumentDateBeforeFilter"
              @update-filter="($e) => debouncedDispatchFilter($e)"
            />
          </template>
          <toggle-filter
            :get-value="getShowControlledSubstancesFilter"
            id="controlled-substances-fil"
            data-test="controlled-substances-fil"
            label="Controlled Substance"
            filter-key="showControlledSubstances"
            filter-action="updateShowControlledSubstancesFilter"
            @update-filter="($e) => debouncedDispatchFilter($e)"
          />
          <toggle-filter
            :get-value="getShowDeletedContainersFilter"
            id="deleted-containers"
            data-test="deleted-containers"
            label="Only Removed Containers"
            filter-key="showDeletedContainers"
            filter-action="updateShowDeletedContainersFilter"
            @update-filter="($e) => debouncedDispatchFilter($e)"
          />
          <short-text-filter
            id="notes-fil"
            data-test="notes-fil"
            label="Notes"
            class="chemtracker-container-text-filter"
            store-watch="getNotesFilter"
            path="field_chemical_note.value"
            filter-key="notesFilter"
            filter-action="updateFilter"
            @update-filter="($e) => debouncedDispatchFilter($e)"
          />
          <multi-select-filter
            :get-value="expiredStatuses"
            id="expired-status-fil"
            data-test="expired-status-fil"
            label="Expired Status"
            display-value="label"
            track-by="key"
            :multiple="false"
            :hovertip="expiredStatusHovertip"
            store-watch="getSelectedExpiredStatusFilter"
            store-commit="setSelectedExpiredStatusFilter"
            filter-key="selectedExpiredStatus"
            filter-action="updateSelectedExpiredStatusFilter"
            @update-filter="($e) => debouncedDispatchFilter($e)"
          />
          <long-text-filter
            v-if="dromoEnabled"
            label="Import ID"
            id="imp-id-fil"
            data-test="imp-id-fil"
            :use-short-text="true"
            :get-value="getImportIdFilter"
            store-watch="getImportIdFilter"
            filter-key="selectedImportId"
            filter-action="updateImportIdFilter"
            @update-filter="($e) => debouncedDispatchFilter($e)"
          />
          <long-text-filter
            :get-value="getContainerIDsFilter"
            id="con-ids-fil"
            data-test="con-ids-fil"
            placeholder="Comma-delimited (or one per line) list of Container IDs"
            store-watch="getContainerIDsFilter"
            label="Container IDs"
            filter-key="containerIDsFilter"
            filter-action="updateContainerIDsFilter"
            @update-filter="($e) => debouncedDispatchFilter($e)"
          />
        </template>
      </VFilterGroup>
      <!-- Table of results that we see below filters -->
      <b-table
        striped
        hover
        :sticky-header="true"
        :no-local-sorting="true"
        :no-sort-reset="true"
        show-empty
        :busy="isBusy"
        :fields="getFields"
        :items="chemContainers"
        :sort-by="sortBy"
        :sort-desc="sortDesc"
        @sort-changed="sortingChangedEvent"
        ref="table"
        :class="{
          'table-pause-scroll': isBusy || chemContainers?.length < 1,
          'table-responsive': responsiveTable,
          'table-fullscreen': !responsiveTable,
        }"
        :current-page="currentPage"
      >
        <template #table-busy>
          <div class="text-center text-danger my-2">
            <div v-if="$isB4">
              <b-spinner />
              <strong>Loading...</strong>
            </div>
            <div v-else>
              <RaftBusy busy-text="Loading..." />
            </div>
          </div>
        </template>
        <template #empty="scope">
          <div class="chemtracker-empty-text">
            {{ emptyStateText }}
          </div>
        </template>
        <!-- Table headers -->
        <template #head()="data">
          <div
            class="column-toggle"
            :class="{ dragging: dragTarget != null }"
            draggable="true"
            @dragenter="($event) => dragEnter($event, data.field.key)"
            @dragstart="($event) => dragStart($event, data.field)"
            @dragend="($event) => dragEnd($event, data.field.key)"
          >
            <input
              type="checkbox"
              :checked="isChecked(data.field.key)"
              @input="(event) => handleColumnCheckbox(event, data.field.key)"
              :key="data?.field?.key"
              v-if="showSettings"
            />
            {{ data?.label }}
            <img
              v-if="showSettings"
              class="draghandle"
              :src="dragSrc"
              alt="drag handle"
            />
          </div>
        </template>
        <!-- Table cells -->
        <template v-if="sds_enabled" #cell(field_chemical_sds)="data">
          <div :class="{ dogged: checkSdsOutdated(data?.item) }">
            {{ formatSdsOrigin(data?.item) }}
          </div>
        </template>
        <template v-if="sds_enabled && canEdit" #cell(field_attach_sds)="data">
          <a
            href="#"
            :rel="'add-sds-' + data?.index"
            :data-test="'add-sds-' + data?.item?.field_chemical_container_id"
            @click="showSdsDialog(data?.item?.id)"
            >Add SDS</a
          >
        </template>
        <template #cell(og_audience[0].title)="data">
          <a
            v-if="data?.item?.og_audience[0]"
            :href="buildNodeUrl(data.item.og_audience[0].drupal_internal__nid)"
          >
            {{ data?.item?.og_audience[0].title }}</a
          >
        </template>
        <template #cell(field_chemical_space.title)="data">
          <a
            v-if="data?.item?.field_chemical_space"
            :href="
              buildNodeUrl(data.item.field_chemical_space.drupal_internal__nid)
            "
          >
            {{ data.item.field_chemical_space.title }}</a
          >
        </template>
        <template #cell(field_chemical_container_id)="data">
          <a :href="buildContainerUrl(data?.item)">{{
            data?.item?.field_chemical_container_id
          }}</a>
        </template>
        <template v-if="canEdit" #cell(chemical_container_edit)="data">
          <a :href="buildContainerUrl(data.item, '/edit')">Edit</a>
        </template>
        <!-- Link name should become "Removed" in red if getShowDeletedContainersFilter is true (CELLS ONLY)-->
        <template
          v-if="getShowDeletedContainersFilter"
          #cell(chemical_container_remove)
        >
          <span id="removedLink">Removed</span>
        </template>
        <template
          v-else-if="canEdit && !getShowDeletedContainersFilter"
          #cell(chemical_container_remove)="data"
        >
          <a @click.prevent="removeContainerConfirm(data.item)" href="#"
            >Remove</a
          >
        </template>
        <!-- End link name -->
        <template #cell(field_chemical_name_preferred)="data">
          <a
            v-if="data?.item?.field_chemical_name_preferred"
            :href="buildChemicalTypeUrl(data.item)"
            >{{ data.item.field_chemical_name_preferred }}</a
          >
        </template>
        <template
          #cell(field_chemical_container_owner.field_prof_display_name)="data"
        >
          <a
            v-if="data?.item?.field_chemical_container_owner"
            :href="buildUserUrl(data.item.field_chemical_container_owner)"
          >
            {{
              data.item.field_chemical_container_owner.field_prof_display_name
            }}</a
          >
        </template>
        <template #cell(field_chemical_note)="data">
          {{ formatNotes(data?.item?.field_chemical_note) }}
        </template>
        <template #cell(view_attached_sds)="row">
          <span v-if="row?.item?.field_chemical_sds">
            <a
              v-if="getSdsLink(row.item.field_chemical_sds) !== ''"
              target="_blank"
              :href="getSdsLink(row.item.field_chemical_sds)"
              >View
            </a>
            <a
              v-else-if="
                row?.item?.field_chemical_sds?.type ===
                'raft_metadata--sds_external'
              "
              href="#"
              @click="
                ChemtrackerSdsHelper.sdsDownloadFile(
                  row.item.field_chemical_sds.field_sds_provider,
                  row.item.field_chemical_sds.field_sds_document_id,
                )
              "
              >View
            </a>
          </span>
        </template>
        <template #cell(field_chemical_expiration)="data">
          {{ data?.item?.field_chemical_expiration }}
        </template>
        <template #cell(field_chemical_expired_status)="data">{{
          getExpiredStatus(data?.item?.field_chemical_expiration)
        }}</template>
      </b-table>
      <b-pagination
        v-if="!isBusy"
        v-model="currentPage"
        :total-rows="totalContainers"
        :per-page="containersPerPage"
        aria-controls="my-table"
        limit="10"
        first-text="« first"
        prev-text="‹ previous"
        next-text="next ›"
        last-text="last »"
        align="center"
        @change="handlePageChange"
      />
    </RAFTFullScreenContent>
    <v-dialog />

    <b-modal
      id="chem-container-sds-attach"
      :title="getSdsModalTitle"
      size="xl"
      scrollable
      centered
      no-close-on-backdrop
      hide-footer
      cancel-disabled
      ok-disabled
      no-stacking
      @close-sds-dialog="hideSdsDialog"
    >
      <ChemicalContainerSdsAttachments
        :selected-container-uuid="sdsSelectedContainerUuid"
      />
    </b-modal>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import draggable from "vuedraggable";
import QS from "qs";
import debounce from "lodash/debounce";
import { useRoute } from "vue-router/composables";
import { usePersistedValues } from "@/composables/usePersistedValues";
import { jsonClient, restClient } from "@/lib/raftApi";
import { useFeatureFlag } from "@/composables/useFeatureFlag";
import { useDrupalSettings } from "@/composables/useDrupalSettings";
import axios from "axios";
import ChemicalContainerSdsAttachments from "./ChemicalContainerSdsAttachments.vue";
import ChemtrackerSdsHelper from "../../lib/ChemtrackerSdsHelper";
import DateFilter from "../filters/DateFilter.vue";
import jsonApiFilterManager from "../../lib/JsonApiFilterManager";
import LongTextFilter from "../filters/LongTextFilter.vue";
import MultiSelectFilter from "../filters/MultiSelectFilter.vue";
import RaftBusy from "../RaftBusy.vue";
import RAFTFullScreenContent from "../RAFTFullScreenContent.vue";
import ShortTextFilter from "../filters/ShortTextFilter.vue";
import ToggleFilter from "../filters/ToggleFilter.vue";
import raftUnits from "../../lib/raft_units";
import VFilterGroup from "../raft/VFilterGroup.vue";
import VActionButton from "../raft/VActionButton.vue";
import dragIcon from "../../images/drag.svg";
import infoIcon from "../../images/info.svg";
import helpIcon from "../raft/help-Outline.svg";
import DromoImportButton from "./DromoImportButton.vue";

const _fields = [
  {
    key: "field_chemical_name_preferred",
    label: "Chemical Name",
    class: "chemical-name-preferred",
    sortable: true,
  },
  {
    key: "field_chemical_type.field_chemdb_cas_number",
    label: "CAS Number",
    class: "chemdb-cas-number",
    sortable: true,
  },
  {
    key: "field_chemical_type.field_chemdb_physical_state",
    label: "State",
    formatter: "formatPhysicalState",
    sortable: true,
  },
  {
    key: "field_chemical_amount",
    label: "Amount",
    formatter: "formatAmount",
    sortable: true,
  },
  { key: "field_chemical_unit_of_measure", label: "Units", sortable: true },
  {
    key: "field_chemical_sds",
    label: "SDS Origin",
    class: "chemical-sds",
    sortable: false,
    tdClass: null,
  },
  {
    key: "field_attach_sds",
    label: "SDS Attachments",
    sortable: false,
  },
  { key: "view_attached_sds", label: "View SDS", sortable: false },
  {
    key: "og_audience[0].title",
    label: "Group",
    class: "chemical-group",
    sortable: true,
    hideOnGroup: "laboratory",
  },
  {
    key: "field_chemical_space.title",
    label: "Location",
    class: "chemical-space",
    sortable: true,
    hideOnGroup: "space",
  },
  {
    key: "field_chemical_container_id",
    label: "Container ID",
    class: "container-id",
    sortable: true,
  },
  { key: "chemical_container_edit", label: "Edit" },
  { key: "chemical_container_remove", label: "Remove" },
  { key: "field_chemical_bench_location", label: "Bench" },
  { key: "field_chemical_shelf_location", label: "Shelf" },
  { key: "field_chemical_room_location", label: "Specific Location Note" },
  {
    key: "changed",
    label: "Date Last Changed",
    formatter: "formatDateString",
    sortable: true,
    sortDirection: "desc",
  },
  {
    key: "field_chemical_date_last_confirm",
    label: "Date Last Confirmed",
    formatter: "formatDateString",
    sortable: true,
    sortDirection: "desc",
  },
  {
    key: "created",
    label: "Submission Date",
    formatter: "formatDateString",
    sortable: true,
    sortDirection: "desc",
  },
  {
    key: "field_chemical_received",
    label: "Date Received",
    formatter: "formatDateReceived",
    sortable: true,
    sortDirection: "desc",
  },
  {
    key: "field_chemical_expiration",
    label: "Expiration Date",
    formatter: "formatDateString",
    sortable: true,
  },
  {
    key: "field_chemical_expired_status",
    label: "Expired Status",
    sortable: false,
  },
  {
    key: "field_chemical_manufacturer.name",
    label: "Manufacturer",
    sortable: true,
  },
  {
    key: "field_chemical_product_name",
    label: "Product Name",
    sortable: true,
  },
  {
    key: "field_chemical_product_number",
    label: "Product Number",
    sortable: true,
  },
  {
    key: "field_chemical_container_status",
    label: "Container Status",
    formatter: "formatContainerStatus",
    sortable: true,
  },
  {
    key: "field_chemical_container_po",
    label: "Purchase Order",
    sortable: true,
  },
  { key: "field_chemical_lot_number", label: "Lot Number", sortable: true },
  {
    key: "field_chemical_container_owner.field_prof_display_name",
    label: "Chemical Owner",
    sortable: false,
  },
  { key: "field_chemical_note", label: "Notes", formatter: "formatNotes" },
];

export default {
  name: "ChemicalContainers",
  setup() {
    const route = useRoute();
    const { config } = useDrupalSettings();
    const { fetchDromoFeature, dromoEnabled, modulesEnabled } =
      useFeatureFlag();
    // hiddenColumns, fields, filters, sort
    const {
      getPersistedValue,
      setPersistedValue,
      deletePersistedValue,
      sessionMetadataList,
      index,
      settings,
      fetchForRoute,
    } = usePersistedValues({
      module: "*",
      route: route.path,
    });

    fetchDromoFeature().then(() => {
      if (dromoEnabled.value) {
        _fields.splice(19, 0, {
          key: "field_chemical_import_id",
          label: "Import ID",
          sortable: true,
        });
      }
    });

    return {
      getPersistedValue,
      setPersistedValue,
      deletePersistedValue,
      sessionMetadataList,
      index,
      settings,
      fetchForRoute,
      fetchDromoFeature,
      dromoEnabled,
      modulesEnabled,
      config,
    };
  },
  data() {
    // TODO - investigate if there's a way to refactor this out into a separate class; this file is much too
    // long and mixes display logic with business logic.
    return {
      newDiv: null,
      emptyStateText: "There are currently no containers to list.",
      desiredUrl: null,
      changesMade: false,
      debouncedUpdateCols: debounce(this.updateCols, 300),
      debouncedContainerUpdate: debounce(
        this.updateInstitutionalChemicalContainersAsync,
        400,
      ),
      debouncedSetPersistedSort: debounce(
        (payload) => this.setPersistedValue(payload),
        300,
      ),
      debouncedSetPersistedUrl: debounce(
        (payload) => this.setPersistedValue(payload),
        300,
      ),
      debouncedSetPersistedHiddenColumns: debounce(
        (payload) => this.setPersistedValue(payload),
        300,
      ),
      debouncedSetPersistedFields: debounce(
        (payload) => this.setPersistedValue(payload),
        300,
      ),
      debouncedDispatchFilter: debounce(this.dispatchFilterUpdate, 400),
      debouncedWindowPushState: debounce(
        (newUrl) => window.history.pushState({}, null, newUrl),
        300,
      ),
      updatedCols: [],
      dragTarget: null,
      dragSrc: dragIcon,
      infoSrc: infoIcon,
      helpSrc: helpIcon,
      showFilters: true,
      showSettings: false,
      isDownloading: false,
      ChemtrackerSdsHelper: ChemtrackerSdsHelper,
      showSdsModal: false,
      sds_enabled: false,
      canEdit: false,
      sdsSelectedContainerUuid: "",
      isBusy: false,
      response: null,
      dateBefore: "",
      dateAfter: "",
      querystring: "",
      containersPerPage: 50,
      pageOffset: 0,
      currentPage: 1,
      updatingContainersStatus: "",
      csvLink: "",
      csvValues: {},
      downloadProgress: 0,
      responsiveTable: true,
      downloadProgressNotificationDuration: 18000,
      endOfDay: 24 * 60 * 60 - 1,
      showEditRemovePerm: "edit any chemical container",
      groupType: "",
      contextId: "",
      fields: _fields,
      chemical_container_fields: [
        "drupal_internal__nid",
        "field_chemical_amount",
        "field_chemical_unit_of_measure",
        "field_chemical_type",
        "field_chemical_space",
        "og_audience",
        "field_chemical_manufacturer",
        "field_chemical_container_id",
        "field_chemical_container_status",
        "created",
        "field_chemical_date_last_confirm",
        "changed",
        "field_chemical_container_type",
        "field_chemical_bench_location",
        "field_chemical_shelf_location",
        "field_chemical_room_location",
        "field_chemical_product_name",
        "field_chemical_product_number",
        "field_chemical_expiration",
        "field_chemical_import_id",
        "field_chemical_received",
        "field_chemical_container_po",
        "field_chemical_lot_number",
        "field_chemical_container_owner",
        "field_chemical_note",
        "field_chemical_dea_controlled",
        "field_chemical_dea_finished_form",
        "field_chemical_dea_contain_size",
        "field_chemical_dea_dosage_units_",
        "field_chemical_name_preferred",
        "field_chemical_sds",
      ],
      dataBaseLinkageStatuses: [
        { key: 1, label: "Linked" },
        { key: 2, label: "Unlinked" },
      ],
      storageGroups: [
        { key: "A", label: "A:Compatible Organic Bases" },
        {
          key: "B",
          label: "B: Compatible Pyrophoric & Water Reactive Materials",
        },
        { key: "C", label: "C: Compatible Inorganic Bases" },
        { key: "D", label: "D: Compatible Organic Acids" },
        { key: "E", label: "E: Compatible Oxidizers including Peroxides" },
        {
          key: "F",
          label:
            "F: Compatible Inorganic Acids not including Oxidizers or Combustibles",
        },
        {
          key: "G",
          label: "G: Not Intrinsically Reactive or Flammable or Combustible",
        },
        { key: "I", label: "I: Compatible Strong, Oxidizing Acids" },
        { key: "J", label: "J: Poison Compressed Gases" },
        {
          key: "K",
          label: "K: Compatible Explosive or other highly Unstable Materials",
        },
        {
          key: "L",
          label:
            "L: Non-Reactive Flammables and Combustibles, including solvents",
        },
        { key: "U", label: "U: Storage group unknown, no data available" },
        { key: "X", label: "X: Incompatible with ALL other storage groups" },
      ],
      physicalStates: raftUnits.physicalStateOptions(),
      hiddenColumns: [],
      purgatory: [],
      databaseLinkageStatusHovertip: {
        id: "databaseLinkageStatusHt",
        title: "Database Linkage Status",
        text:
          "Linked chemical containers are associated" +
          "to a chemical from the ChemTracker chemical database. These types of containers have hazard" +
          "and regulatory data included in the system. Unlinked chemical containers are associated to an" +
          "institution-specific chemical and do not have hazard or regulatory data included in ChemTracker." +
          "Unlinked chemical containers can be edited to connected to a chemical in the database.",
      },
      lastUpdateAfterHovertip: {
        id: "lastUpdateAfterHt",
        title: "Date Last Updated",
        text: "Date Last Updated is the date a chemical record was last edited in the system.",
      },
      chemicalOwnerHovertip: {
        id: "chemOwnerHt",
        title: "Chemical Owners",
        text: "This shows all people who are listed as chemical owners. This list will be blank if no containers here have a chemical owner associated.",
      },
      sdsOriginStatuses: [
        { key: "sds_external", label: "Centralized Database" },
        { key: "sds_file", label: "Local Upload" },
        { key: "sds_url", label: "URL" },
        { key: "outdated", label: "Outdated" },
        { key: "no_attachment", label: "No Attachment" },
      ],
      sortBy: "field_chemical_name_preferred",
      sortDesc: false,
      expiredStatuses: [
        { key: "any", label: "Any" },
        { key: "expired", label: "Expired" },
        { key: "not_expired", label: "Not Expired" },
        { key: "date_blank", label: "Date Blank" },
      ],
      expiredStatusHovertip: {
        id: "expiredStatusHt",
        title: "Expired Status",
        text: "The ‘Expired’ status only shows containers that have an expiration date filled out.",
      },
    };
  },
  provide() {
    return {
      contextNid: this.contextNid,
      isB4: this.$isB4,
    };
  },
  components: {
    ChemicalContainerSdsAttachments,
    RAFTFullScreenContent,
    MultiSelectFilter,
    DateFilter,
    ShortTextFilter,
    ToggleFilter,
    LongTextFilter,
    RaftBusy,
    draggable,
    VActionButton,
    VFilterGroup,
    DromoImportButton,
  },
  async mounted() {
    await this.fetchDromoFeature();
    // @todo RAFT-6464 Make these requests earlier in the user's session, so they don't block render.
    await this.fetchForRoute(["url", "hiddenColumns", "sort", "fields"]);
    this.applyGroupFilter();
    // By default we want to disable the space filter.
    // However there are some cases where we want it to work since
    // we will not have a building filter to fill it from.
    const options = [
      "laboratory",
      "department",
      "space",
      "building",
      "raft_unit",
    ];
    if (options.indexOf(this.getGroupType) > -1) {
      this.updateSpaceFilterDisabled(false);
    } else {
      this.updateSpaceFilterDisabled(true);
    }

    this.getFilterItemsAsync();
    const persistedSort = await this.getPersistedValue("sort");
    if (persistedSort.value) {
      this.sortingChangedEvent(persistedSort.value);
    }

    const persistedFields = await this.getPersistedValue("fields");
    if (
      persistedFields.value &&
      persistedFields.value.length >= this.fields?.length
    ) {
      this.fields = persistedFields.value;
    }
    this.adjustFieldsForSds();
    this.adjustFieldsForEditing();

    jsonApiFilterManager.queries["page[limit]"] = this.containersPerPage;
    jsonApiFilterManager.queries["fields[node--chemical_container]"] =
      this.chemical_container_fields.join();

    const persistedHiddenColumns =
      await this.getPersistedValue("hiddenColumns");
    this.hiddenColumns = persistedHiddenColumns.value || [];
    this.hideColumns();

    this.addSdsWatchers();

    if (Object.keys(this.queryStringFilters()).length <= 0) {
      const lastUrl = await this.getPersistedValue("url");
      if (lastUrl.value) {
        try {
          window.history.pushState({}, null, lastUrl.value);
        } catch (e) {
          // eslint-disable-next-line
          console.error(e);
        }
      }
    }
    this.dispatchActionsFromUrl(window.location.search.slice(1));
    return this.debouncedContainerUpdate();
  },
  methods: {
    ...mapActions([
      "clearAll",
      "updateShowDeletedContainersFilter",
      "updateSelectedBuildings",
      "updateSpaceFilterDisabled",
      "updateContainerIDsFilter",
      "fetchBuildingList",
      "fetchBuildingSpaceList",
      "fetchBuildingLaboratoriesList",
      "fetchGroupSpaceList",
      "fetchSpaceLaboratoriesList",
      "fetchDepartmentList",
      "fetchLaboratoriesList",
      "fetchCampusList",
      "fetchSpaceList",
      "fetchChemicalHazardsList",
      "fetchChemicalOwningUsers",
      "updateSelectedDatabaseLinkageFilter",
      "setSelectedExpiredStatusFilter",
      "updateSelectedExpiredStatusFilter",
      "updateShowControlledSubstancesFilter",
      "updateUpdatedAfterDateFilter",
      "updateUpdatedBeforeDateFilter",
      "updateSpacesQueryString",
      "clearListedSpaces",
      "getBuildingQueryAdditions",
      "updateShowSdsModal",
      "updateContainerExpiresAfterFilter",
      "updateContainerExpiresBeforeFilter",
      "updateSdsDocumentDateAfterFilter",
      "updateSdsDocumentDateBeforeFilter",
      "updateSdsOriginFilter",
      "updateMultivalueFilter",
    ]),
    adjustFieldsForSds() {
      this.sds_enabled = this.getSdsUiEnabled;
      this.canEdit = this.config.userChemPerms.includes(
        this.showEditRemovePerm,
      );
      if (!this.sds_enabled) {
        this.fields = this.fields.filter(
          (field) =>
            field.key !== "field_chemical_sds" &&
            field.key !== "field_attach_sds" &&
            field.key !== "view_attached_sds",
        );
      } else {
        const field_index = this.fields.findIndex(
          (field) => field.key === "field_chemical_sds",
        );
        this.fields[field_index].tdClass = this.sdsOriginClass;
      }
    },
    adjustFieldsForEditing() {
      if (!this.canEdit) {
        for (let i = 0; i < this.fields?.length; i++) {
          if (this.fields[i] != null && this.fields[i].key != null) {
            if (this.fields[i].key === "chemical_container_remove") {
              delete this.fields[i];
              // eslint-disable-next-line no-continue
              continue;
            }
            if (this.fields[i].key === "chemical_container_edit") {
              delete this.fields[i];
            }
          }
        }
      }
    },
    addSdsWatchers() {
      this.$store.watch(
        (state, getters) => getters.getShowSdsModal,
        (showSdsModal) => {
          if (showSdsModal === false) {
            this.hideSdsDialog();
          }
        },
      );
      this.$store.watch(
        (state, getters) => getters.getSdsBulkAssignStatus,
        (sdsBulkAssignStatus) => {
          if (
            sdsBulkAssignStatus.hasOwnProperty("type") &&
            sdsBulkAssignStatus.hasOwnProperty("title")
          ) {
            this.$showNotification(
              sdsBulkAssignStatus.type,
              sdsBulkAssignStatus.title,
            );
          }
        },
      );
    },
    readHiddenCount() {
      this.newDiv = document.createElement("div");
      this.newDiv.className = "visuallyhidden";
      this.newDiv.setAttribute("role", "alert");
      this.newDiv.appendChild(
        document.createTextNode(
          "Filters have been applied but will not all be saved when refreshing the page. " +
            this.displayAtoB +
            " " +
            this.totalContainers +
            " results. " +
            this.emptyStateText,
        ),
      );
      document.body.appendChild(this.newDiv);
      setTimeout(() => {
        document.removeChild(this.newDiv);
      }, 2000);
    },
    updateRole() {
      const newRole = document.querySelector(
        ".b-table-empty-row > td:nth-child(1) > div:nth-child(1)",
      );
      if (newRole) {
        newRole.setAttribute("role", "status");
      }
    },
    queryStringFilters() {
      return QS.parse(window.location.search.slice(1));
    },
    applyGroupFilter() {
      if (
        this.getGroupType === "laboratory" ||
        this.getGroupType === "department" ||
        this.getGroupType === "raft_unit"
      ) {
        return this.updateMultivalueFilter({
          filterKey: "selectedLabs",
          filterAction: "updateMultivalueFilter",
          trackBy: "nid",
          path: "og_audience.drupal_internal__nid",
          operator: "IN",
          value: [{ nid: this.contextNid }],
          useJsonApiFilterManager: true,
        });
      }
      if (this.getGroupType === "space") {
        return this.updateMultivalueFilter({
          filterKey: "selectedSpaces",
          filterAction: "updateMultivalueFilter",
          trackBy: "drupal_internal__nid",
          path: "field_chemical_space.drupal_internal__nid",
          operator: "IN",
          value: [{ drupal_internal__nid: this.contextNid }],
          useJsonApiFilterManager: true,
        });
      }
      if (this.getGroupType === "building") {
        return this.updateMultivalueFilter({
          filterKey: "selectedBuildings",
          filterAction: "updateMultivalueFilter",
          trackBy: "drupal_internal__nid",
          path: "field_chemical_space.field_parent_location.drupal_internal__nid",
          operator: "IN",
          value: [{ drupal_internal__nid: this.contextNid }],
          useJsonApiFilterManager: true,
        });
      }
    },
    handlePageChange(newPage) {
      jsonApiFilterManager.queries["page[offset]"] =
        (newPage - 1) * this.containersPerPage;
      return this.debouncedContainerUpdate();
    },
    async resetDefaults() {
      this.$set(this, "fields", _fields);
      this.debouncedSetPersistedFields({
        key: "fields",
        newValue: _fields,
      });
      this.hiddenColumns = [];
      this.purgatory = [];
      this.debouncedSetPersistedHiddenColumns({
        key: "hiddenColumns",
        newValue: [],
      });
      this.sortBy = "field_chemical_name_preferred";
      this.debouncedSetPersistedSort({
        key: "sort",
        newValue: "field_chemical_name_preferred",
      });
      this.toggleShowSettings();
    },
    async clearFilters() {
      const newUrl = `${this.$route.path}`;
      window.history.pushState({}, null, newUrl);
      this.setPersistedValue({ key: "url", newValue: newUrl });
      await this.clearAll();
      this.$store.dispatch("updateShowDeletedContainersFilter", {
        value: false,
      });
      this.applyGroupFilter();
      this.debouncedContainerUpdate();
    },
    async dispatchFilterUpdate($e) {
      // Reset the pager to be the first page of results.
      this.currentPage = 1;
      // Reset the jsonapi offset to zero to get the first group of results.
      jsonApiFilterManager.queries["page[offset]"] = 0;
      await this.$store.dispatch($e.filterAction, $e);
      return this.updateUrlFromFilter($e);
    },
    handleColumnCheckbox($event, columnKey) {
      if ($event.target.checked) {
        const indexToRemove = this.purgatory.findIndex(
          (i) => i.key === columnKey,
        );
        this.purgatory.splice(indexToRemove, 1);
      } else {
        this.purgatory.push(this.fields.find((i) => i.key === columnKey));
      }
      this.changesMade = true;
    },
    updateUrlFromFilter($event) {
      const current = QS.parse(window.location.search.slice(1));
      delete current[$event.filterKey];
      const value = $event.value ?? "";
      const isDate = value && value instanceof Date;
      const stringNotEmpty = value && typeof value === "string" && value !== "";
      const arrayOrObjectNotEmpty =
        value &&
        typeof value === "object" &&
        (value.length > 0 || Object.entries(value).length > 0);
      const isBoolean = typeof value === "boolean";
      if (isDate || stringNotEmpty || arrayOrObjectNotEmpty || isBoolean) {
        current[$event.filterKey] = $event;
      }
      const querystring = QS.stringify(current);
      const newUrl = `${this.$route.path}?${querystring}`;
      const size = new Blob([newUrl]).size;
      if (size < 8192) {
        this.debouncedSetPersistedUrl({ key: "url", newValue: newUrl });
        this.debouncedWindowPushState(newUrl);
      } else {
        this.$showNotification(
          "warn",
          "Filters have been applied, but will not all be saved when refreshing the page.",
        );
        // Timeout for screen readers
        setTimeout(() => {
          this.readHiddenCount();
        }, 1000);
      }
      this.debouncedContainerUpdate();
    },
    dispatchActionsFromUrl(search) {
      const current = QS.parse(search);
      const actionSet = new Set(Object.keys(this.$store._actions));
      for (const key of Object.keys(current)) {
        if (actionSet.has(current[key].filterAction)) {
          this.$store.dispatch(current[key].filterAction, current[key]);
        }
      }
    },
    isChecked(columnKey) {
      return this.purgatory?.findIndex((i) => i.key === columnKey) < 0;
    },
    dragEnter($event, columnKey) {
      if (this.showSettings) {
        const column = this.fields.find((i) => i.key === columnKey);
        return this.debouncedUpdateCols(column);
      }
    },
    dragStart($event, columnKey) {
      if (this.showSettings) {
        this.dragTarget = columnKey;
      }
    },
    dragEnd($event, columnKey) {
      if (this.showSettings) {
        const column = this.fields.find((i) => i.key === columnKey);
        this.dragTarget = column; // have to use the variable to pass linter :-O
        // This is a sloppy way of blur'ing a single element
        // (the one in the place that the dragged element comes from)
        const dropElements = document.getElementsByClassName("column-toggle");
        Array.prototype.forEach.call(dropElements, (element) => element.blur());
        this.dragTarget = null;
      }
    },
    updateCols(columnField) {
      const columnsDataCopy = [...this.fields];
      const moveFrom = this.fields.findIndex(
        (i) => i.key === this.dragTarget?.key,
      );
      const moveTo = this.fields.findIndex((i) => i?.key === columnField.key);
      const temp = columnsDataCopy[moveFrom];
      if (moveFrom < moveTo) {
        for (let i = moveFrom + 1; i <= moveTo; i++)
          this.$set(columnsDataCopy, i - 1, columnsDataCopy[i]);
      } else {
        for (let i = moveFrom - 1; i >= moveTo; i--)
          this.$set(columnsDataCopy, i + 1, columnsDataCopy[i]);
      }
      this.$set(columnsDataCopy, moveTo, temp);
      this.updatedCols = columnsDataCopy.filter(
        (col) => col && col.hasOwnProperty("key"),
      );
      this.fields = this.updatedCols;
      this.$emit("change-col-order", this.updatedCols);
      this.debouncedSetPersistedFields({
        key: "fields",
        newValue: this.fields,
      });
    },
    checkSdsOutdated(item) {
      if (
        item?.field_chemical_sds &&
        !item?.field_chemical_sds?.field_current
      ) {
        return true;
      }
      return false;
    },
    showSdsDialog(selectedContainerUuid) {
      this.updateShowSdsModal(true);
      this.sdsSelectedContainerUuid = selectedContainerUuid;
      this.$bvModal.show("chem-container-sds-attach");
    },
    hideSdsDialog() {
      this.sdsSelectedContainerUuid = "";
      this.$bvModal.hide("chem-container-sds-attach");
    },
    getInstitutionalChemicalContainersQueryFiltersObject(jsonApiQueries) {
      if (!jsonApiQueries) {
        jsonApiQueries = jsonApiFilterManager.queries;
      }
      if (
        this.sds_enabled &&
        !jsonApiQueries.include.match(
          /field_chemical_sds.field_external_sds_file/,
        )
      ) {
        if (!jsonApiQueries.include) {
          jsonApiQueries = {
            ...jsonApiQueries,
            include: "field_chemical_sds.field_external_sds_file",
          };
        } else {
          jsonApiQueries.include +=
            ",field_chemical_sds.field_external_sds_file";
        }
      }
      for (const key in jsonApiQueries) {
        const keyString = key.toString();
        if (
          keyString.endsWith("[storeKey]") ||
          keyString.endsWith("[storeValue]")
        ) {
          delete jsonApiQueries[key];
        }
      }
      return jsonApiQueries;
    },
    updateInstitutionalChemicalContainers(filters) {
      this.isBusy = false;
      this.toggleBusy();
      // TODO - move this to the chemtracker client.
      let path = "/node/chemical_container";
      const queries =
        this.getInstitutionalChemicalContainersQueryFiltersObject(filters);
      if (this.contextNid) {
        path += `?rog-type=node&rog-id=${this.contextNid}`;
      }
      return jsonClient
        .cancelRequest(path)
        .getAsPost({ path, queries })
        .catch((e) => {
          if (!axios.isCancel(e)) {
            // eslint-disable-next-line
            console.error(e);
          }
        })
        .then((response) => {
          if (response) {
            this.response = response;
            this.isBusy = true;
            this.toggleBusy();
            return Promise.resolve();
          }
          return Promise.reject(new Error("Empty response"));
        });
    },
    getInstitutionalChemicalContainersQueryFilters(jsonApiQueries) {
      if (!jsonApiQueries) {
        jsonApiQueries = jsonApiFilterManager.queries;
      }
      if (
        this.sds_enabled &&
        !jsonApiQueries.include.match(
          /field_chemical_sds.field_external_sds_file/,
        )
      ) {
        jsonApiQueries.include += ",field_chemical_sds.field_external_sds_file";
      }
      Object.keys(jsonApiQueries).forEach((key) => {
        const keyString = key.toString();
        if (
          keyString.endsWith("[storeKey]") ||
          keyString.endsWith("[storeValue]")
        ) {
          delete jsonApiQueries[key];
        }
      });
      this.querystring = Object.keys(jsonApiQueries)
        .map((key) => key + "=" + encodeURIComponent(jsonApiQueries[key]))
        .join("&");
      return this.querystring;
    },
    // Handle the Date Received field differently from other dates, because the value is not in ISO 8601 format,
    // and the JS Date() object parses it differently, resulting in a date one day off.
    formatDateReceived(value) {
      if (!value) {
        return;
      }
      let date = new Date(value);
      date =
        date.getUTCFullYear() +
        "-" +
        (date.getUTCMonth() + 1).toString().padStart(2, "0") +
        "-" +
        date.getUTCDate().toString().padStart(2, "0");
      return date;
    },
    formatDateString(value) {
      if (!value) {
        return;
      }
      let date = new Date(value);
      date =
        date.getFullYear() +
        "-" +
        (date.getMonth() + 1).toString().padStart(2, "0") +
        "-" +
        date.getDate().toString().padStart(2, "0");
      return date;
    },
    formatAmount(value) {
      return Number(value);
    },
    formatPhysicalState(value) {
      const states = raftUnits.physicalStates();
      return states[value];
    },
    formatSdsOrigin(item) {
      const type = item?.field_chemical_sds?.type;
      if (!type) {
        return "";
      }
      let value;

      switch (type.replace(/raft_metadata--/, "")) {
        case "sds_external":
          value = "Centralized Database";
          break;
        case "sds_file":
          value = "Local Upload";
          break;
        case "sds_url":
          value = "URL";
          break;
        default:
          value = type;
      }
      if (!item?.field_chemical_sds?.field_current) {
        value = "Outdated (" + value + ")";
      }
      return value;
    },
    sdsOriginClass(value, key, item) {
      const classes = [];
      if (
        item?.field_chemical_sds?.type &&
        !item.field_chemical_sds?.field_current
      ) {
        // Use this class to be consistent with B3 dog ear styling.
        classes.push("date_approaching");
      }
      return classes;
    },
    buildUserUrl(user) {
      return "/user/" + user?.drupal_internal__uid;
    },
    buildNodeUrl(nid) {
      return "/node/" + nid;
    },
    buildContainerUrl(container, suffix) {
      const groupContext = this.getGroupContextString();
      let url = "";
      if (this.$isB4) {
        url = this.config.baseUrl + "/node/" + container.drupal_internal__nid;
      } else if (this.contextNid) {
        url =
          this.config.b3baseUrl +
          "/node/" +
          this.contextNid +
          "/chemtracker/container/" +
          container.id;
      } else {
        url =
          this.config.b3baseUrl + "/raft/chemtracker/container/" + container.id;
      }
      if (suffix) {
        url += suffix;
      }
      return url + groupContext;
    },
    buildChemicalTypeUrl(container) {
      const groupContext = this.getGroupContextString();
      if (this.$isB4) {
        return (
          this.config.baseUrl +
          "/node/" +
          container.field_chemical_type.drupal_internal__nid +
          groupContext
        );
      }
      return (
        this.config.b3baseUrl +
        "/raft/chemdb/chemical/" +
        container.field_chemical_type.id +
        groupContext
      );
    },
    formatContainerStatus(status) {
      // @see \Drupal\chemical_container\ChemicalContainerHelper.
      switch (status) {
        case 0:
          return "Hidden";

        case 1:
          return "Normal";

        case 2:
          return "Surplus";

        default:
          return "";
      }
    },
    formatNotes(notes) {
      if (notes !== null && typeof notes === "object") {
        return notes.value;
      }
      return notes;
    },
    async updateInstitutionalChemicalContainersAsync(filters) {
      try {
        await this.updateInstitutionalChemicalContainers(filters);
        this.updatingContainersStatus = "success";
        this.updateRole();
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
        this.updatingContainersStatus = "error";
        this.isBusy = true;
        this.toggleBusy();
      }
    },
    hideColumns() {
      this.debouncedSetPersistedHiddenColumns({
        key: "hiddenColumns",
        newValue: this.hiddenColumns,
      });
      const validFields =
        this.fields?.length > 0
          ? this.fields?.filter((f) => typeof f !== "undefined")
          : [];
      const newFields = [...validFields];
      newFields.forEach((obj) => {
        if (obj) {
          obj.thClass = "";
          obj.tdClass = "";
        }
      });
      if (this.hiddenColumns.length > 0) {
        const hiddenColumnsKeys = this.hiddenColumns.map((col) => col?.key);
        newFields.forEach((obj) => {
          if (hiddenColumnsKeys.includes(obj?.key)) {
            obj.thClass = "d-none";
            obj.tdClass = "d-none";
          }
        });
      }
      this.$set(this, "fields", newFields);
      this.$refs?.table?.refresh();
      this.changesMade = false;
    },
    getFilterItemsAsync() {
      try {
        const option = [this.getGroupType];
        // check to get the appropriate space list.
        if (option.indexOf("building") > -1) {
          this.fetchBuildingSpaceList();
        } else if (option.indexOf("laboratory") > -1) {
          this.fetchGroupSpaceList();
        } else if (option.indexOf("department") > -1) {
          this.fetchGroupSpaceList();
        } else if (option.indexOf("raft_unit") > -1) {
          this.fetchGroupSpaceList();
        } else {
          this.fetchBuildingList();
        }
        this.fetchChemicalOwningUsers();
        this.fetchCampusList();
        // Check to get the appropriate lab/group list.
        if (option.indexOf("building") > -1) {
          this.fetchBuildingLaboratoriesList();
        } else if (option.indexOf("space") > -1) {
          this.fetchSpaceLaboratoriesList();
        } else {
          this.fetchLaboratoriesList();
        }
        this.fetchDepartmentList();
        this.fetchChemicalHazardsList();
      } catch (e) {
        this.$emit("form-error-message", {
          message: "Failed to load data.",
          data: e,
        });
      }
    },
    sortingChangedEvent(sortEvent) {
      if (typeof sortEvent?.sortBy !== "undefined") {
        this.debouncedSetPersistedSort({ key: "sort", newValue: sortEvent });
        delete jsonApiFilterManager.queries.sort;
        if (sortEvent.sortBy === "og_audience[0].title") {
          sortEvent.sortBy = "og_audience.title";
        }

        this.sortBy = sortEvent.sortBy;
        this.sortDesc = sortEvent.sortDesc;
        const sortPrefix = sortEvent.sortDesc ? "-" : "";
        jsonApiFilterManager.queries.sort =
          sortPrefix + sortEvent.sortBy + ",field_chemical_name_preferred";
        this.debouncedContainerUpdate();
      }
    },
    toggleBusy: function () {
      this.isBusy = !this.isBusy;
      this.tableResetScroll();
      this.$refs?.table?.refresh();
    },
    toggleShowFilters() {
      this.showFilters = !this.showFilters;
      if (this.showFilters && this.showSettings) {
        this.toggleShowSettings();
      }
    },
    toggleShowSettings() {
      this.showSettings = !this.showSettings;
      if (this.showSettings) {
        this.showFilters = false;
        this.purgatory = this.hiddenColumns;
        this.hiddenColumns = [];
        this.hideColumns();
      } else {
        this.hiddenColumns = this.purgatory;
        this.purgatory = [];
        this.hideColumns();
      }
    },
    tableResetScroll: function () {
      this.$refs?.table?.$el?.scrollTo(0, 0);
    },
    removeContainerConfirm: function (container) {
      this.$modal.show("dialog", {
        title: "",
        text:
          "Are you sure you want to delete container " +
          container.field_chemical_container_id +
          "?",
        buttons: [
          {
            title: "Cancel",
            handler: () => {
              this.$modal.hide("dialog");
            },
          },
          {
            title: "Remove",
            handler: () => {
              this.$modal.hide("dialog");
              this.removeContainer(container);
            },
          },
        ],
      });
    },
    async removeContainer(container) {
      const editData = {
        id: container.id,
        type: "node--chemical_container",
        attributes: {
          operative_status: 0,
        },
      };
      const response = await jsonClient
        .patch({
          path: `/node/chemical_container/${container.id}`,
          data: editData,
          queries: {},
        })
        .catch((e) => {
          this.$showNotification(
            "error",
            `Problem removing container: ${e.message}`,
          );
        });
      if (response) {
        this.$showNotification(
          "success",
          "Container " +
            container.field_chemical_container_id +
            " was successfully removed.",
        );
        this.updateInstitutionalChemicalContainersAsync();
      }
    },
    async postToCsvDownload() {
      let path = "/raft/chemicals/containers/hybrid-download";
      if (this.contextNid) {
        path += "?rog-type=node&rog-id=" + this.contextNid;
      }

      this.downloadProgress = 10;
      this.$Progress.set(this.downloadProgress);
      this.$showNotification(
        "default",
        "Generating Chemical Container Export with " +
          this.totalContainers +
          " Rows",
      );
      const csvDownloadStateChange = () => {
        // We fake the progress bar since we don't know the content length.
        this.$Progress.pause();
        if (this.downloadProgress < 90) {
          this.downloadProgress += 0.1;
        } else if (this.downloadProgress < 98) {
          this.downloadProgress += 0.01;
        }
        this.$Progress.set(this.downloadProgress);
      };
      const queries = { ...jsonApiFilterManager.queries };
      // Remove some parts not used for export.
      delete queries.include;
      delete queries["page[limit]"];
      delete queries["fields[node--chemical_container]"];
      await restClient.downloadFileStream({
        path,
        filename: "Chemical_Container_export.csv",
        queries,
        extraOptions: { onDownloadProgress: csvDownloadStateChange },
      });
      this.$Progress.finish();
    },
    getGroupContextString: function () {
      let groupContext = "";
      if (this.contextNid) {
        groupContext = "?rog-type=node&rog-id=" + this.contextNid;
      }
      return groupContext;
    },
    updateOpenClosedFullscreen(newVal) {
      this.responsiveTable = !newVal;
    },
    getSdsLink(sds_entity) {
      const type = sds_entity.type.replace(/raft_metadata--/, "") || "";
      if (type === "sds_url") {
        return sds_entity.field_chemdb_type_related_url.uri;
      }
      let file_field = "";
      if (type === "sds_external") {
        file_field = "field_external_sds_file";
      } else if (type === "sds_file") {
        file_field = "field_chemdb_type_related_file";
      }
      if (
        !file_field ||
        !sds_entity[file_field] ||
        !sds_entity[file_field].hasOwnProperty("uri")
      ) {
        return "";
      }
      let url = sds_entity[file_field].uri.url;
      if (url && !this.$isB4) {
        url = this.config.baseUrl + url + "?jwt=" + this.config.fileJwt;
      }
      return url;
    },
    getExpiredStatus(dateString) {
      if (dateString) {
        // Parse date string into a Date object
        const today = new Date();
        // If expired, return "Expired"
        if (dateString < today.toISOString().substring(0, 10)) {
          return "Expired";
        }
        return "Not Expired";
      }
      return "";
    },
  },
  props: {
    contextNid: { type: String, default: null },
  },
  computed: {
    ...mapGetters([
      "config",
      "allBuildings",
      "allSpaces",
      "allLabs",
      "allDepartments",
      "allCampuses",
      "allChemicalHazards",
      "filterCount",
      "getSelectedBuildingsFilter",
      "getContainerIDsFilter",
      "getShowDeletedContainersFilter",
      "getSelectedDatabaseLinkageFilter",
      "getSelectedExpiredStatusFilter",
      "getShowControlledSubstancesFilter",
      "getUpdatedAfterDateFilter",
      "getUpdatedBeforeDateFilter",
      "getJsonApiFilters",
      "getShowSdsModal",
      "getSdsUiEnabled",
      "getSdsModalTitle",
      "getContainerExpiresAfterFilter",
      "getContainerExpiresBeforeFilter",
      "getSdsDocumentDateAfterFilter",
      "getSdsDocumentDateBeforeFilter",
      "getSdsOriginFilter",
      "getSpaceFilterDisabled",
      "getChemOwningUserOptions",
      "getImportIdFilter",
    ]),
    showDromoButton() {
      return this.config.isDromoContext;
    },
    chemicalOwnerOptionsLimit() {
      if (this.contextNid) {
        return 1000;
      }
      return 100;
    },
    displayAtoB() {
      return `Displaying ${this.displayingCurrent} - ${this.displayingCurrentTotal} of`;
    },
    adjustedFilterCount() {
      return this.getGroupType !== "" ? this.filterCount - 1 : this.filterCount;
    },
    chemContainers() {
      const chemContainers = this.response ? this.response?.deserialized : [];
      if (chemContainers?.length < 1) {
        this.tableResetScroll();
      }
      return chemContainers;
    },
    filterManagerQueries() {
      return jsonApiFilterManager.queries;
    },
    getGroupType() {
      let groupType = "";
      if (!this.$isB4) {
        groupType = Drupal?.settings?.bioraft?.group_type_machine_name;
      } else {
        groupType = drupalSettings?.chemtracker?.context_type;
      }
      return groupType;
    },
    totalContainers() {
      return this.response?.meta?.count || 0;
    },
    totalPages() {
      return Math.ceil(this.totalContainers / this.containersPerPage) || 0;
    },
    displayingCurrent: function () {
      if (this.currentPage > 1) {
        return (this.currentPage - 1) * this.containersPerPage + 1;
      }
      return 1;
    },
    displayingCurrentTotal: function () {
      if (this.totalContainers < this.containersPerPage) {
        return this.totalContainers;
      }
      if (this.totalPages === this.currentPage) {
        return this.totalContainers;
      }
      return this.displayingCurrent + this.containersPerPage - 1;
    },
    getLabCheck: function () {
      let test = true;
      const options = ["laboratory", "department", "raft_unit"];
      if (options.indexOf(this.getGroupType) > -1) {
        test = false;
      }
      return test;
    },
    getSpaceCheck: function () {
      let test = true;
      const options = ["space"];
      if (options.indexOf(this.getGroupType) > -1) {
        test = false;
      }
      return test;
    },
    getBuildingCheck: function () {
      let test = true;
      const options = [
        "laboratory",
        "department",
        "space",
        "building",
        "raft_unit",
      ];
      if (options.indexOf(this.getGroupType) > -1) {
        test = false;
      }
      return test;
    },
    getFields() {
      let fields = this.fields;
      if (this.getGroupType) {
        fields = fields.filter(
          (field) => field?.hideOnGroup !== this.getGroupType,
        );
      }
      const removeIndex = fields.findIndex(
        (f) => f?.key === "chemical_container_remove",
      );
      if (removeIndex > -1) {
        fields[removeIndex].label = this.getShowDeletedContainersFilter
          ? "Removed"
          : "Remove";
      }
      const dateIndex = fields.findIndex((f) => f?.key === "changed");
      if (dateIndex > -1) {
        fields[dateIndex].label = this.getShowDeletedContainersFilter
          ? "Date Removed"
          : "Date Last Changed";
      }
      if (this.getShowDeletedContainersFilter) {
        fields = fields.filter(
          (field) => field.key !== "chemical_container_edit",
        );
      }
      return fields.map((field) => ({
        ...field,
        formatter: this[field?.formatter],
      }));
    },
  },
};
</script>

<style scoped>
* {
  box-sizing: border-box;
}

img.draghandle {
  position: relative;
  top: 0.35rem;
}

#chemical-containers >>> td.date_approaching {
  color: #4e4e50;
  background-color: #fff8d2;
  position: static;
}

td div.dogged {
  width: 100%;
  height: 100%;
  background: linear-gradient(
    -135deg,
    #4e4e50 0%,
    #4e4e50 10%,
    transparent 10%,
    transparent 100%
  );
  margin-inline-end: 1rem;
}

#chemical-containers >>> th:first-of-type {
  z-index: 5 !important;
}

#removedLink {
  color: var(--red-900);
}

#chemical-containers >>> div.table-responsive {
  overflow: scroll;
}
</style>

<style>
#con-ids-fil {
  resize: none;
  height: 8.125rem !important;
}
div.flex-left {
  display: flex;
  column-gap: 0.25rem;
}
</style>
